import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { ethers, getBigInt } from "ethers";
import { toast, ToastContainer } from "react-toastify";
import { FaCirclePlus, FaRegCirclePlay } from "react-icons/fa6";

import { Puff } from "react-loader-spinner";
// Components
import { Button, Modal } from "react-bootstrap";
import { Tooltip } from "react-tooltip";

// Hooks and Utils
import useDocumentTitle from "../../../components/useDocumentTitle";
import { CreateAuction, CreateBulkAuction } from "../../../hooks/AuctionHook";
import { collectionABI } from "../../../config/ABIs/CollectionABI";
import { auction_contract_address } from "../../../config/BackendUrl";

// Assets
import logo1 from "../../../assets/img/avatars/avatar_1.png";
import logo from "../../../assets/img/logos/dtoc-logo.png";
import { getCollectionListbyWalletAddress } from "../../../hooks/Collectionhook";
import { AddActivityNFT, listNFTs, updateNFT } from "../../../hooks/NFTHook";
import { handleApiError, retryApiCall } from '../../../utils/apiUtils';

const AuctionForm = () => {
  useDocumentTitle("Mint");
  const navigate = useNavigate();
  const PROVIDER = useSelector((state) => state.valueFromHeader);
  const walletAddress = localStorage.getItem("WalletAddress");
  const [isTransacting, setIsTransacting] = useState(false);

  // State Management
  const [state, setState] = useState({
    nfts: [],
    collection: [],
    selectedNFT: [],
    selectedItems: [],
    tokenIds: [],
    endTime: "",
    selectedNFTs: new Set(), // Using Set for better performance with selections
    selectedCollection: null,
    isSelectAll: false,
    showTooltip: false,
    isHovered: false,
    selectCollection: false,
    toggle: false,
  });

  // Modal States
  const [modals, setModals] = useState({
    approvalModal: false,
    timeSelectModal: false,
  });

  const pendingToast = (message) => {
    return toast(
      <div className="d-flex align-items-center">
        <Puff size={20} color="#36d7b7" />
        <span className="ml-2">{message}</span>
      </div>,
      {
        position: "bottom-right",
        autoClose: false,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: false,
        progress: undefined,
      }
    );
  };

  // Fetch Collections
  const fetchCollections = useCallback(async () => {
    try {
      const query = { ownerAddress: walletAddress, isDraft: false, sortBy: "createdAt" };
      const { success, data } = await
        getCollectionListbyWalletAddress(query);
      console.log("success, data", success, data);

      if (success) {
        const collectionsWithMarketData = await Promise.all(
          data.map(async (collection) => {
            const marketData = await retryApiCall(
              () => fetchMarketData(collection.contractAddress),
              3,
              1000
            );
            return {
              ...collection,
              marketData: marketData?.total > 0 ? marketData.data : [],
            };
          })
        );
        setState((prev) => ({
          ...prev,
          collection: collectionsWithMarketData,
        }));
      }
    } catch (error) {
      handleApiError(error, "Failed to fetch collections");
    }
  }, [walletAddress]);

  const fetchMarketData = async (collectionAddress) => {
    try {
      const params = {
        listed: true,
        inAuction: false,
        inFractionNFt: false,
        draftStatus: false,
        contractAddress: collectionAddress,
        sortBy: "createdAt",
      };
      const response = await listNFTs(params);
      return response.success ? response : { success: false, data: [] };
    } catch (error) {
      console.error("Error fetching market data:", error);
      return { success: false, data: [] };
    }
  };

  // Collection Selection
  const handleCollectionSelect = async (collection) => {
    if (collection.marketData.length === 0) return;

    const updatedNFTs =
      state.collection.find(
        (c) => c.contractAddress === collection.contractAddress
      )?.marketData || [];

    setState((prev) => ({
      ...prev,
      selectedCollection: collection,
      nfts: updatedNFTs,
      selectCollection: true,
      toggle: true,
    }));
  };

  // Contract Interactions
  const handleSetApproval = async () => {
    const toastId = pendingToast("Approving collection...");
    setIsTransacting(true);

    try {
      const signer = await PROVIDER.getSigner();
      const contract = new ethers.Contract(
        state.selectedCollection.contractAddress,
        collectionABI,
        signer
      );

      const tx = await contract.setApprovalForAll(
        auction_contract_address,
        true
      );
      // Update toast while waiting for confirmation
      toast.update(toastId, {
        render: (
          <div className="d-flex align-items-center">
            <Puff size={20} color="#36d7b7" />
            <span className="ml-2">Waiting for confirmation...</span>
          </div>
        ),
      });
      const receipt = await tx.wait();

      if (receipt) {
        toast.dismiss(toastId);
        toast.success("Approval successful!");

        closeModals();

        if (state.selectedNFTs.size === 1) {
          await createSingleAuction();
        } else {
          await createBulkAuction();
        }
      }
    } catch (error) {
      toast.dismiss(toastId);
      toast.error("Failed to approve: " + (error.message || "Unknown error"));
      console.error("Approval error:", error);
    }
  };

  const createSingleAuction = async () => {
    const toastId = pendingToast("Creating auction...");
    setIsTransacting(true);

    try {
      const epochTime = new Date(state.endTime).getTime() / 1000;
      const body = {
        collectionAddress: state.selectedCollection.contractAddress,
        tokenId: Array.from(state.selectedNFTs)[0],
        endTime: epochTime,
      };

      const { data } = await CreateAuction(body);
      console.log("xadta", data);

      toast.update(toastId, {
        render: (
          <div className="d-flex align-items-center">
            <Puff size={20} color="#36d7b7" />
            <span className="ml-2">Processing transaction...</span>
          </div>
        ),
      });

      const signer = await PROVIDER.getSigner(walletAddress);
      const tx = await signer.sendTransaction(data);

      toast.update(toastId, {
        render: (
          <div className="d-flex align-items-center">
            <Puff size={20} color="#36d7b7" />
            <span className="ml-2">Waiting for confirmation...</span>
          </div>
        ),
      });

      const receipt = await tx.wait();

      // if (receipt) {
      //   toast.success("Auction created successfully!");
      //   setTimeout(() => navigate("/live-auctions"), 2000);
      // }
      if (receipt) {
        toast.dismiss(toastId);
        const transferEventSignatures = ethers.keccak256(
          ethers.toUtf8Bytes("Transfer(address,address,uint256)")
        );
        // Filter logs for Transfer events
        const logs = receipt.logs.filter(
          (log) => log.topics[0] === transferEventSignatures
        );
        console.log("Step 5 => logs", logs);

        let tokenId, from, to, transactionHash;
        if (logs.length > 0) {
          logs.forEach(async (log) => {
            // Extract tokenId directly from topics[3]
            const rawFrom = log.topics[1];
            const rawTo = log.topics[2];
            // Decode the 'from' and 'to' addresses, handling zero address scenarios
            from =
              rawFrom !== `0x${"0".repeat(64)}`
                ? ethers.getAddress(rawFrom.slice(-40))
                : "Minting (no from)";
            to =
              rawTo !== `0x${"0".repeat(64)}`
                ? ethers.getAddress(rawTo.slice(-40))
                : "Burning (no to)";

            transactionHash = log.transactionHash;
            // tokenId = getBigInt(log.topics[3]).toString();
            console.log(
              "Step 6 => Minted Token ID:",
              tokenId,
              from,
              to,
              transactionHash
            );

            const NFTbody = {
              inAuction: true,
            };

            console.log("Step 7 => NFTbody", NFTbody);
            const NFTId = state.selectedCollection.marketData.filter(
              (data) => data.tokenId === Array.from(state.selectedNFTs)[0]
            )[0]._id;
            await updateNFT(NFTId, NFTbody);
            const activityBody = {
              event: "Auctioned",
              price: 0,
              from: from,
              to: to,
            };
            await AddActivityNFT(NFTId, activityBody).then((data) => {
              toast.success("Auction created successfully!");
              setTimeout(() => navigate("/live-auctions"), 2000);
            });
          });
        } else {
          console.error("Unable to find Transfer event in raw logs.");
        }
      }
    } catch (error) {
      toast.dismiss(toastId);
      toast.error(
        "Failed to create auction: " + (error.message || "Unknown error")
      );
      console.error("Auction creation error:", error);
    } finally {
      setIsTransacting(false);
    }
  };

  // const createBulkAuction = async () => {
  //   const toastId = pendingToast("Creating bulk auction...");
  //   setIsTransacting(true);

  //   try {
  //     const epochTime = new Date(state.endTime).getTime() / 1000;
  //     const body = {
  //       collectionAddress: state.selectedCollection.contractAddress,
  //       tokenIds: Array.from(state.selectedNFTs),
  //       endTime: epochTime,
  //     };

  //     const { data } = await CreateBulkAuction(body);

  //     toast.update(toastId, {
  //       render: (
  //         <div className="d-flex align-items-center">
  //           <Puff size={20} color="#36d7b7" />
  //           <span className="ml-2">Processing transactions...</span>
  //         </div>
  //       ),
  //     });

  //     const signer = await PROVIDER.getSigner(walletAddress);
  //     const tx = await signer.sendTransaction(data);

  //     toast.update(toastId, {
  //       render: (
  //         <div className="d-flex align-items-center">
  //           <Puff size={20} color="#36d7b7" />
  //           <span className="ml-2">Waiting for confirmation...</span>
  //         </div>
  //       ),
  //     });

  //     const receipt = await tx.wait();

  //     if (receipt) {
  //       toast.dismiss(toastId);
  //       toast.success("Bulk auction created successfully!");
  //       setTimeout(() => navigate("/live-auctions"), 2000);
  //     }
  //   } catch (error) {
  //     toast.dismiss(toastId);
  //     toast.error(
  //       "Failed to create bulk auction: " + (error.message || "Unknown error")
  //     );
  //     console.error("Bulk auction creation error:", error);
  //   } finally {
  //     setIsTransacting(false);
  //   }
  // };

  // Selection Handlers
  const createBulkAuction = async () => {
    const toastId = pendingToast("Creating bulk auction...");
    setIsTransacting(true);
  
    try {
      const epochTime = Math.floor(new Date(state.endTime).getTime() / 1000);
      const body = {
        collectionAddress: state.selectedCollection.contractAddress,
        tokenIds: Array.from(state.selectedNFTs), // Ensure this is an array of numbers/strings
        endTime: epochTime,
      };
  
      console.log("Auction body:", body);
  
      const { data } = await CreateBulkAuction(body);
  
      console.log("Transaction data from API:", data);
  
      if (!data || !data.to || !data.data) {
        throw new Error("Invalid transaction data from API");
      }
  
      toast.update(toastId, {
        render: (
          <div className="d-flex align-items-center">
            <Puff size={20} color="#36d7b7" />
            <span className="ml-2">Processing transactions...</span>
          </div>
        ),
      });
  
      const signer = await PROVIDER.getSigner(); // Avoid passing walletAddress if unnecessary
      console.log("Signer:", signer);
      const gasEstimate = await signer.estimateGas({
        to: data.to,
        data: data.data,
        value: data.value || "0x0",
      });
      console.log("Estimated Gas:", gasEstimate.toString());
      const tx = await signer.sendTransaction({
        to: data.to, // Ensure this is the contract address
        data: data.data, // Ensure this is properly encoded call data
        value: data.value || "0x0", // Ensure it's a valid value, usually 0 for contract calls
      });
  
      console.log("Transaction hash:", tx.hash);
  
      toast.update(toastId, {
        render: (
          <div className="d-flex align-items-center">
            <Puff size={20} color="#36d7b7" />
            <span className="ml-2">Waiting for confirmation...</span>
          </div>
        ),
      });
  
      const receipt = await tx.wait();
  
      if (receipt && receipt.status === 1) {
        toast.dismiss(toastId);
        toast.success("Bulk auction created successfully!");
        setTimeout(() => navigate("/live-auctions"), 2000);
      } else {
        throw new Error("Transaction failed on-chain");
      }
    } catch (error) {
      console.error("Bulk auction creation error:", error);
      toast.dismiss(toastId);
      toast.error(
        "Failed to create bulk auction: " + (error.message || "Unknown error")
      );
    } finally {
      setIsTransacting(false);
    }
  };
  
  const handleSelectAll = () => {
    setState((prev) => {
      const allTokenIds = prev.nfts.map((nft) => nft.tokenId);
      const newSelectedNFTs = new Set(
        prev.selectedNFTs.size === allTokenIds.length ? [] : allTokenIds
      );

      return {
        ...prev,
        selectedNFTs: newSelectedNFTs,
      };
    });
  };

  const handleSelectNFT = (tokenId) => {
    setState((prev) => {
      const newSelectedNFTs = new Set(prev.selectedNFTs);
      if (newSelectedNFTs.has(tokenId)) {
        newSelectedNFTs.delete(tokenId);
      } else {
        newSelectedNFTs.add(tokenId);
      }

      return {
        ...prev,
        selectedNFTs: newSelectedNFTs,
      };
    });
  };
  
  // Helper function to check if all NFTs are selected
  const areAllNFTsSelected = () => {
    return (
      state.nfts.length > 0 && state.selectedNFTs.size === state.nfts.length
    );
  };

  // Helper function to check if a specific NFT is selected
  const isNFTSelected = (tokenId) => {
    return state.selectedNFTs.has(tokenId);
  };

  // Modal Handlers
  const openModal = (type) => {
    setModals((prev) => ({ ...prev, [type]: true }));
  };

  const closeModals = () => {
    setModals({ approvalModal: false, timeSelectModal: false });
  };

  // Effects
  useEffect(() => {
    fetchCollections();
  }, [fetchCollections]);

  // Render Helpers
  const renderNFTImage = (nft) => {
    if (nft.assetType.split("/")[0] !== "image") {
      return (
        <div
          className="video-wrapper avatar avatar-lg"
          onMouseEnter={() =>
            setState((prev) => ({ ...prev, isHovered: true }))
          }
          onMouseLeave={() =>
            setState((prev) => ({ ...prev, isHovered: false }))
          }
        >
          <video
            className={`video ${state.isHovered ? "playing" : ""
              } avatar avatar-lg `}
            style={{ borderRadius: "18px" }}
            muted
            loop
            playsInline
            onMouseEnter={(e) => e.target.play()}
            onMouseLeave={(e) => e.target.pause()}
          >
            <source src={nft.imageUri} type={nft.assetType} />
            Your browser does not support video.
          </video>
          {!state.isHovered && (
            <div className="pause-overlay">
              <FaRegCirclePlay />
            </div>
          )}
        </div>
      );
    }

    return (
      <img
        src={nft.imageUri}
        alt={nft.title}
        style={{ borderRadius: "18px" }}
        className="avatar avatar-lg"
        onError={(e) => (e.target.src = logo1)}
      />
    );
  };

  return (
    <div
      className="container"
      style={{ pointerEvents: isTransacting ? "none" : "auto" }}
    >
      <button
        onClick={() => navigate(-1)}
        className="btn btn-white btn-sm my-40 ml-4"
      >
        Back
      </button>

      <div className="box in__upload mt-40 mb-120">
        {!state.selectCollection ? (
          <CollectionSelection
            collections={state.collection}
            onSelect={handleCollectionSelect}
            showTooltip={state.showTooltip}
          />
        ) : (
          <NFTSelection
            nfts={state.nfts}
            selectedNFTs={state.selectedNFTs}
            onSelectAll={handleSelectAll}
            onSelectNFT={handleSelectNFT}
            areAllSelected={areAllNFTsSelected()}
            isSelected={isNFTSelected}
            onTimeSelect={() => openModal("timeSelectModal")}
            renderNFTImage={renderNFTImage}
            selectedCollection={state.selectedCollection}
          />
        )}
      </div>

      {/* <LoadingOverlay /> */}

      <ToastContainer
        position="bottom-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <ApprovalModal
        show={modals.approvalModal}
        onClose={() => closeModals()}
        onApprove={handleSetApproval}
        logo={logo}
      />

      <TimeSelectModal
        show={modals.timeSelectModal}
        onClose={() => closeModals()}
        endTime={state.endTime}
        onTimeChange={(time) =>
          setState((prev) => ({ ...prev, endTime: time }))
        }
        onSubmit={() => openModal("approvalModal")}
      />
    </div>
  );
};

// Subcomponents
// ... [Previous imports and main AuctionForm component remain the same until the subcomponents] ...

const CollectionSelection = ({ collections, onSelect, showTooltip }) => {
  return (
    <div className="row">
      <div className="col-lg-12">
        <div className="form-group space-y-10">
          <div className="space-y-20">
            <div className="space-y-10 drop_ui">
              <h2 className="variationInput">Choose collection</h2>
              <div className="row">
                {collections.map((collection, i) => (
                  <div
                    className={`col-xl-4 col-lg-4 col-md-6 col-sm-6 ${collection.marketData.length === 0 ? "disabled-card" : ""
                      }`}
                    key={i}
                    onClick={() =>
                      collection.marketData.length !== 0 && onSelect(collection)
                    }
                    data-tooltip-id="my-tooltip"
                  >
                    <div className="card__item four">
                      <div className="card_body space-y-10 d-flex align-items-center justify-content-start">
                        <div className="avatars space-x-3">
                          <img
                            src={collection.logo}
                            alt={collection.name}
                            onError={(e) => (e.target.src = logo1)}
                            className="avatar avatar-lg"
                          />
                        </div>
                        <div className="mx-3">
                          <h6 className="card_title">
                            <div className="color_black">{collection.name}</div>
                          </h6>
                          <div className="card_footer d-block space-y-10">
                            <div className="card_footer justify-content-between">
                              <span className="card_title">
                                {collection.symbol}
                              </span>
                            </div>
                          </div>
                          <div>
                            <h5>
                              Available NFT:{" "}
                              <span className="text-primary">
                                {collection.marketData.length}
                              </span>
                            </h5>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {showTooltip && (
                  <Tooltip id="my-tooltip" place="top" effect="solid">
                    Please add an NFT!
                  </Tooltip>
                )}

                <CreateCollectionCard />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const CreateCollectionCard = () => {
  return (
    <div className="col-xl-4 col-lg-4 col-md-6 col-sm-6">
      <div className="card__item four">
        <Link
          to="/collectionAdd"
          className="card_body space-y-10 d-flex align-items-center justify-content-start"
        >
          <div className="avatars space-x-3">
            <FaCirclePlus color="black" size="65" />
          </div>
          <div className="mx-3">
            <h6 className="card_title">
              <span className="color_black">Create</span>
            </h6>
            <div className="card_footer d-block space-y-10">
              <div className="card_footer justify-content-between">
                <span className="card_title">ERC-721</span>
              </div>
            </div>
          </div>
        </Link>
      </div>
    </div>
  );
};

// Updated NFTSelection component
const NFTSelection = ({
  nfts,
  selectedNFTs,
  onSelectAll,
  onSelectNFT,
  areAllSelected,
  isSelected,
  onTimeSelect,
  renderNFTImage,
  selectedCollection,
}) => {
  return (
    <div className="row">
      <div className="col-lg-12">
        <div className="form-group space-y-10">
          <div className="space-y-20">
            <div className="space-y-10 drop_ui">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h2 className="variationInput">
                  Choose NFT For Auction from {selectedCollection.name}{" "}
                  Collection
                </h2>
                <div className="selection-info">
                  <span className="text-primary">
                    Selected: {selectedNFTs.size} of {nfts.length} NFTs
                  </span>
                </div>
              </div>

              {nfts.length > 0 ? (
                <>
                  <NFTTable
                    nfts={nfts}
                    areAllSelected={areAllSelected}
                    isSelected={isSelected}
                    onSelectAll={onSelectAll}
                    onSelectNFT={onSelectNFT}
                    renderNFTImage={renderNFTImage}
                  />
                  <div className="d-flex justify-content-between mt-4">
                    <CreateNFTCard collectionName={selectedCollection.name} />
                    <ActionButtons
                      onCancel={() => { }}
                      onAuction={onTimeSelect}
                      disabled={selectedNFTs.size === 0}
                    />
                  </div>
                </>
              ) : (
                <CreateNFTCard collectionName={selectedCollection.name} />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Updated NFTTable component with improved selection handling
const NFTTable = ({
  nfts,
  areAllSelected,
  isSelected,
  onSelectAll,
  onSelectNFT,
  renderNFTImage,
}) => {
  return (
    <div className="table-responsive">
      <table className="table">
        <thead>
          <tr>
            <th width="50">
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={areAllSelected}
                  onChange={onSelectAll}
                  id="selectAllNFTs"
                />
                <p className="form-check-label" htmlFor="selectAllNFTs">
                  All
                </p>
              </div>
            </th>
            <th>Asset</th>
            <th>Title</th>
            <th>Category</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          {nfts.map((nft) => (
            <tr
              key={nft.tokenId}
              className={isSelected(nft.tokenId) ? "table-active" : ""}
            >
              <td>
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={isSelected(nft.tokenId)}
                    onChange={() => onSelectNFT(nft.tokenId)}
                    id={`nft-${nft.tokenId}`}
                  />
                </div>
              </td>
              <td>
                <div className="avatars">{renderNFTImage(nft)}</div>
              </td>
              <td>
                <div className="d-flex flex-column">
                  <span className="fw-bold">{nft.title}</span>
                  <small className="text-muted">#{nft.tokenId}</small>
                </div>
              </td>
              <td>{nft.category}</td>
              <td>
                <div className="text-truncate" style={{ maxWidth: "200px" }}>
                  {nft.description}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const CreateNFTCard = ({ collectionName }) => {
  return (
    <div className="col-xl-4 col-lg-4 col-md-6 col-sm-6">
      <div className="card__item four">
        <Link
          to="/upload"
          className="card_body space-y-10 d-flex align-items-center justify-content-start"
        >
          <div className="avatars space-x-3">
            <FaCirclePlus color="black" size="65" />
          </div>
          <div className="mx-3">
            <h6 className="card_title">
              <span className="color_black">Create NFT</span>
            </h6>
            <div className="card_footer d-block space-y-10">
              <div className="card_footer justify-content-between">
                <span className="card_title">{collectionName}</span>
              </div>
            </div>
          </div>
        </Link>
      </div>
    </div>
  );
};

// Updated ActionButtons component with disabled state
const ActionButtons = ({ onCancel, onAuction, disabled }) => {
  return (
    <div className="acton-buttons d-flex ga-2">
      <button className="btn btn-sm btn-outline-primary" onClick={onCancel}>
        Cancel
      </button>
      <button
        className="btn btn-sm btn-primary mx-2"
        onClick={onAuction}
        disabled={disabled}
      >
        Create Auction
      </button>
    </div>
  );
};

const ApprovalModal = ({ show, onClose, onApprove, logo }) => {
  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <img src={logo} style={{ width: "8rem" }} alt="Logo" />
        </Modal.Title>
      </Modal.Header>
      <div className="m-4">
        <h4>Are you want to Update Approval State?</h4>
      </div>
      <Modal.Footer>
        <Button onClick={onClose}>No</Button>
        <Button onClick={onApprove}>Yes</Button>
      </Modal.Footer>
    </Modal>
  );
};

const TimeSelectModal = ({
  show,
  onClose,
  endTime,
  onTimeChange,
  onSubmit,
}) => {
  return (
    <Modal show={show} size="lg" onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Auction</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <h4>Please add End Time</h4>
        <p>
          <input
            type="datetime-local"
            value={endTime}
            onChange={(e) => onTimeChange(e.target.value)}
            min={new Date().toISOString().slice(0, -8)}
          />
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={onSubmit}>Auction</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AuctionForm;
